import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { RiAddLine, RiArrowDropDownFill, RiSubtractLine } from "react-icons/ri"
import { Controller } from "react-hook-form"
import { components } from "react-select"
import Select from "react-select"
import CreatableSelect from "react-select/creatable"
import { ButtonActions } from "../Button"
import { CardPhoto } from "../Card"
import { AddOptions } from "../Drag"
import { IconFilter } from "../ImageComponents"
import { Column } from "../Layout"
import AsyncSelect from "react-select/async"
import "./styles.scss"
import { VariableSizeList as List } from "react-window";

export const ContainerForm = ({ formClass, children }) => {
  return <div className={formClass}>{children}</div>
}

export const ContainerInput = ({
  inputClass,
  children,
  hasValue = false,
  hasError = false,
  width,
}) => {
  const feedback = `${hasError ? `input-invalid` : hasValue && !hasError ? `input-valid` : ""}`
  return (
    <div
      className={feedback ? `${inputClass}${" "}${feedback}` : inputClass}
      style={width && { width: `${width}rem` }}>
      {children}
      {(hasError || hasValue) && <span className="input-icon-feedback"></span>}
      {hasError && <div className="input-invalid-feedback">{hasError}</div>}
    </div>
  )
}

export const ContainerInputFilter = ({ inputClass, children }) => {
  return (
    <div className={inputClass}>
      {children}
      <ButtonActions type={"filter"}>
        <IconFilter />
      </ButtonActions>
    </div>
  )
}

export const WrapperInputContent = ({ children }) => {
  return <div className="wrapper-input-content">{children}</div>
}

export const FormRow = ({ children, key, className }) => {
  return (
    <div key={key} className={(className && `${"form-row"}${" "}${className}`) || "form-row"}>
      {children}
    </div>
  )
}

export const FormBox = ({ children, className }) => {
  return (
    <div className={(className && `${"form-box"}${" "}${className}`) || "form-box"}>{children}</div>
  )
}

export const FormGroup = ({ children }) => {
  return <ContainerForm formClass={"form-group"}>{children}</ContainerForm>
}

export const Input = ({
  type,
  tabindex,
  placeholder,
  hasError,
  hasValue,
  max,
  isDisabled = false,
  isWhite,
  hasFocus = true,
  register,
  required,
  autoComplete,
  hasIcon,
  min,
  step,
  size,
  handleClick,
  name,
  iconClick = "fi fi-rr-menu-dots-vertical",
  ...field
}) => {
  
  const inputSize = size ? `form-control input-${size} input-field` : "input-field"
  return (
    <ContainerInput
      inputClass={!hasFocus ? "input input-container--normal" : "input input-container"}
      hasError={hasError}
      hasValue={hasValue}>
      <div
        className={`${
          !size
            ? (!isWhite && "input-content") || `input-content input-content--white`
            : "form-control-input"
        }`}>
        {hasIcon && <div className="input-icon">{hasIcon}</div>}
        <input
          type={type || "text"}
          className={inputSize}
          placeholder={placeholder}
          maxLength={max}
          min={min}
          step={step}
          disabled={isDisabled}
          required={required}
          tabIndex={tabindex}
          autoComplete={autoComplete && autoComplete}
          name={name}
          {...register}
          {...field}
        />
        {typeof handleClick === "function" && (
          <button
            className="input-icon handle-filter-input-button"
            onClick={handleClick}
            style={isWhite && { backgroundColor: "#fff" }}>
            <i className={iconClick}></i>
          </button>
        )}
      </div>
    </ContainerInput>
  )
}

export const InputTextArea = ({
  type,
  tabindex,
  placeholder,
  hasError,
  hasValue,
  max,
  isDisabled = false,
  isNormal = false,
  isWhite,
  hasFocus = true,
  register,
  required,
  autoComplete,
  hasIcon,
  min,
  step,
  onChange,
  resize,
  background,
  height,
  width,
  ...field
}) => {
  return (
    <div className={!hasFocus ? "input-text-area" : "input-text-area input-text-area-focus"}>
      <textarea
        className={`${(!isWhite && "input-text-area--secondary") || `input-text-area--white`}`}
        placeholder={placeholder}
        onChange={onChange}
        {...register}
        {...field}
        disabled={isDisabled}
        style={{ resize: resize, background: background, width: width, height }}
      />
    </div>
  )
}

export const TextArea = ({
  height,
  width,
  resize,
  placeholder,
  name,
  value,
  defaultValue,
  onChange,
  isDisabled,
  background,
  isWhite,
  hasFocus = false,
  register,
  ...field
}) => {
  return (
    <div className={!hasFocus ? "input-text-area" : "input-text-area input-text-area-focus"}>
      <textarea
        className={`${(!isWhite && "input-text-area--secondary") || `input-text-area--white`}`}
        placeholder={placeholder}
        name={name}
        value={value}
        defaultValue={value || defaultValue}
        onChange={onChange}
        {...register}
        disabled={isDisabled}
        {...field}
        style={{ resize: resize, background: background, width: width, height }}
      />
    </div>
  )
}

export const TextAreaAutoSize = ({
  height,
  width,
  resize,
  placeholder,
  name,
  value,
  defaultValue,
  onChange,
  isDisabled,
  background,
  isWhite,
  hasFocus = false,
  register,
  ref,
  isAutoScroll = false,
  isAutofocus = false,
  ...field
}) => {
  const textAreaRef = useRef()

  const useAutosizeTextArea = (textAreaRef, value) => {
    useEffect(() => {
      if (textAreaRef && value) {
        textAreaRef.style.height = "0px"
        const scrollHeight = textAreaRef.scrollHeight
        textAreaRef.style.height = scrollHeight + "px"
      }
    }, [textAreaRef, value])
  }

  isAutoScroll && useAutosizeTextArea(textAreaRef.current, value)

  useEffect(() => {
    if (isAutofocus) {
      textAreaRef.current.focus()
    }
  }, [isAutofocus])

  return (
    <div className={!hasFocus ? "input-text-area" : "input-text-area input-text-area-focus"}>
      <textarea
        className={`${(!isWhite && "input-text-area--secondary") || `input-text-area--white`}`}
        placeholder={placeholder}
        name={name}
        value={value}
        defaultValue={value || defaultValue}
        onChange={onChange}
        {...register}
        ref={textAreaRef}
        {...field}
        disabled={isDisabled}
        style={{ resize: resize, background: background, width: width, height }}
      />
    </div>
  )
}

export const InputMetricText = ({ name, value, onChange, isDisabled, isWhite, control }) => {
  const optionsMetric = [
    { value: "1", label: "Kg" },
    { value: "2", label: "Cm" },
    { value: "3", label: "mm" },
    { value: "4", label: "pol" },
  ]

  return (
    <ContainerInput inputClass="input-metric-text">
      <Column className={"col-5 p-0"}>
        <Label color={"white"} label={"Digite o valor"} noFocus />
        <Input
          type={"number"}
          onChange={onChange}
          value={value}
          name={name}
          placeholder={"00"}
          isWhite={isWhite}
          isDisabled={isDisabled}
        />
      </Column>
      <Column className={"col-5 pr-0"}>
        <Label color={"white"} label={"Unidade de Medida"} />
        <SelectInput
          name="metric"
          options={optionsMetric}
          isWhite={isWhite}
          isDisabled={isDisabled}
          control={control}
        />
      </Column>
    </ContainerInput>
  )
}

export const NumberInputCount = ({
  placeholder,
  name,
  value,
  onChange,
  error,
  max,
  isDisabled,
  handleClickInc,
  handleClickDec,
}) => {
  return (
    <ContainerInput inputClass="number-input-count" error={error}>
      <button className="button__count button__count--decrement" onClick={handleClickDec}>
        <RiSubtractLine size={20} />
      </button>
      <input
        type={"text"}
        className="input__number__count"
        placeholder={placeholder}
        name={name}
        defaultValue={value}
        onChange={onChange}
        maxLength={max}
        disabled={isDisabled}
      />
      <button className="button__count button__count--increment" onClick={handleClickInc}>
        <RiAddLine size={20} />
      </button>
    </ContainerInput>
  )
}

export const FilterInput = ({ name, placeholder, type, value, onChange, max }) => {
  return (
    <ContainerInputFilter inputClass="input-filter-container">
      <input
        type={type || "text"}
        className="input-filter"
        placeholder={placeholder || "Pesquisar indicador..."}
        name={name}
        value={value}
        onChange={onChange}
        maxLength={max}
      />
    </ContainerInputFilter>
  )
}

export const QuestionInput = ({ type, onChange, control, optionsDrag, setOptionsDrag }) => {
  return (
    <div className="questions-database">
      <div className="content-questions-input">
        {type === "text" && (
          <WrapperInputContent>
            <Input type={type} onChange={onChange} placeholder={"Digite aqui"} isWhite isDisabled />
          </WrapperInputContent>
        )}
        {type === "email" && (
          <WrapperInputContent>
            <Column className={"col-8 p-0"}>
              <Label color={"white"} label={"Digite o email"} />
              <Input
                type={"type"}
                onChange={onChange}
                placeholder={"exemplo@maneje.com"}
                isWhite
                isDisabled
              />
            </Column>
          </WrapperInputContent>
        )}
        {type === "url" && (
          <WrapperInputContent>
            <Column className={"col-8 p-0"}>
              <Label color={"white"} label={"Link"} noFocus />
              <Input
                type={"type"}
                onChange={onChange}
                placeholder={"Cole o link aqui"}
                isWhite
                isDisabled
              />
            </Column>
          </WrapperInputContent>
        )}
        {type === "tel" && (
          <WrapperInputContent>
            <Column className={"col-6 p-0"}>
              <Label color={"white"} label={"Telefone"} noFocus />
              <Input
                type={"type"}
                onChange={onChange}
                placeholder={"(99) 9999-9999"}
                isWhite
                isDisabled
              />
            </Column>
          </WrapperInputContent>
        )}
        {type === "users" && (
          <WrapperInputContent>
            <Column className={"col-8"}>
              <Label color={"white"} label={"Usuários"} noFocus />
              <SelectInput name="metric" options={[""]} control={control} isWhite />
            </Column>
          </WrapperInputContent>
        )}
        {type === "textArea" && (
          <WrapperInputContent>
            <TextArea
              resize={"none"}
              type={type}
              onChange={onChange}
              placeholder={"Digite aqui"}
              isWhite
              isDisabled
            />
          </WrapperInputContent>
        )}
        {type === "number" && <NumberInputCount onChange={onChange} isDisabled value={0} />}
        {type === "file" && <CardPhoto isWhite isDisabled />}
        {type === "date" && (
          <WrapperInputContent>
            <Column className={"col-7"}>
              <Input type={"date"} isWhite isDisabled />
            </Column>
          </WrapperInputContent>
        )}
        {type === "checkbox" && <AddOptions options={optionsDrag} setOptions={setOptionsDrag} />}
        {type === "radio" && (
          <AddOptions options={optionsDrag} limit={5} setOptions={setOptionsDrag} type={"radio"} />
        )}
        {type === "metric" && (
          <WrapperInputContent>
            <InputMetricText onChange={onChange} control={control} isDisabled value={0} />
          </WrapperInputContent>
        )}
      </div>
    </div>
  )
}

export const Label = ({
  hasError,
  label,
  subtitle,
  children,
  marginLeft,
  marginRight,
  color,
  noFocus,
  forInput,
  className,
  dangerousHtmlInSubTitle = false,
  dangerousHtmlInTitle = false,
  noMargin,
}) => {
  const labelClass = `label-container label-container--${color || (hasError ? "error" : "dark-gray")} ${
    noFocus ? "nofocus" : "focus"
  }${className ? " " + className : ""}`;

  const labelStyle = noMargin ? { margin: 0 } : { marginLeft, marginRight };

  return (
    <label className={labelClass} htmlFor={forInput} style={labelStyle}>
      {label && (
        !dangerousHtmlInTitle ? (
          <span className={`label-container__title ${label ? "mb0" : ""}`}>{label}</span>
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: label }}
            className="label-container__title"
          />
        )
      )}
      {subtitle && (
        dangerousHtmlInSubTitle ? (
          <div
            dangerouslySetInnerHTML={{ __html: subtitle }}
            className="label-container__subtitle"
          />
        ) : (
          <span className="label-container__subtitle">{subtitle}</span>
        )
      )}
      {children}
    </label>
  );
};

const CaretDownIcon = () => {
  return (
    <div className="options__indicator-icon">
      <RiArrowDropDownFill />
    </div>
  )
}

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <CaretDownIcon />
    </components.DropdownIndicator>
  )
}

const NoOptionsMessage = (props) => {
  return (
    <components.NoOptionsMessage {...props}>
      <span className="custom-css-class">Sem Opções</span>
    </components.NoOptionsMessage>
  )
}

const MenuList = ({ options, children, maxHeight, getValue }) => {
  const value = getValue();
  const listRef = useRef(null);
  const itemHeights = useRef({});

  const setItemSize = useCallback((index, size) => {
    itemHeights.current[index] = size;
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, []);

  const getItemSize = (index) => itemHeights.current[index] || 35;
  
  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [children]);

  const Row = ({ index, style }) => (
    <div
      style={{ ...style, height: "auto", display: "flex" }}
      ref={(el) => {
        if (el) {
          const height = el.getBoundingClientRect().height;
          if (height !== itemHeights.current[index]) {
            setItemSize(index, height);
          }
        }
      }}
    >
      {children[index]}
    </div>
  );

  return (
    <List
      ref={listRef}
      height={Math.min(options.length * 35, maxHeight)}
      itemCount={children.length}
      itemSize={getItemSize}
      className="virtualized-select-list"
      width="100%"
    >
      {Row}
    </List>
  );
};

export const SelectInput = ({
  options,
  name,
  control,
  isRequired = false,
  handleChange,
  placeholder,
  isMulti,
  isOverModal = false,
  whiteVariation,
  closeMenuOnSelect = true,
  isSearchable = false,
  isDisabled = false,
  isClearable = false,
  defaultValue,
  isLoading,
  selectedValue,
  hasError = false,
  value,
  ref,
  register,
  findValue = false,
  menuShouldBlockScroll = false,
  breakLine = true,
  resetFields,
  resetDefaultValue = false,
  className = "",
  maxHeight = 0,
  isVirtualized = false,
  ...field
}) => {

  const valueContainerRef = useRef(null);

  const customStyles = {
    control: (base, state) => ({
      ...base,
      background: !whiteVariation ? "#EFEFEF" : "#ffff !important",
      fontSize: "13px",
      lineHeight: "150%",
      color: !whiteVariation ? "#87938A" : "#394F3F !important",
      border: "none",
      boxShadow: state.isFocused ? null : null,
      "&:hover": {
        borderColor: state.isFocused ? "red" : "blue",
      },
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999999 }),
    menu: (provided) => ({ ...provided, zIndex: 999999 }),
    menuList: (base) => ({
      ...base,
      maxHeight: '150px', // Altura fixa
      overflowY: 'auto', // Permitir rolagem
      paddingTop: -1,
      paddingBottom: -1,
      marginLeft: 0,
      marginRight: 0,
      marginBottom: 0,
      marginTop: 3,
      border: "1px solid #EFEFEF",
      borderRadius: "4px",
    }),
    noOptionsMessage: (base) => ({
      ...base,
    }),
  }

  const dynamicHeight = maxHeight || "150px";

  const customStylesScroll = {
    ...customStyles,
    valueContainer: (provided) => ({
      ...provided,
      overflowY: 'auto',
      maxHeight: dynamicHeight,
    }),
    menuList: (provided) => ({
      ...provided,
      overflowY: 'auto',
    }),
  };

  const selectRef = useRef(null)

  const onClear = () => {
    selectRef?.current?.clearValue()
  }

  useEffect(() => {
    if (resetFields) {
      onClear()
    }
  }, [resetFields])

  useEffect(() => {
    if (resetDefaultValue) {
      selectRef?.current?.setValue(options[0])
    }
  }, [resetDefaultValue])

  const getValues = () => {
    if (isMulti && value && value.length) {
      return options?.filter(
        (c) =>
          Array.isArray(value) && value.find((f) => f.value === c.value || f.label === c.value),
      )
    } else {
      return options?.find((c) => c.value === value || c.value === value?.value)
    }
  }

  const customComponents = isVirtualized ? { DropdownIndicator, NoOptionsMessage, MenuList } : { DropdownIndicator, NoOptionsMessage }

  if (!control) {
    return (
      <div className={`${breakLine ? "react-select-container" : "react-select-container-no-wrap"}`}>
        <Select
          className={`options-select ${isMulti ? "multi" : ""} ${className}`}
          classNamePrefix="options"
          components={customComponents}
          isSearchable={isSearchable}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isMulti={isMulti}
          isLoading={isLoading}
          menuShouldBlockScroll={menuShouldBlockScroll}
          menuPortalTarget={isOverModal && document.body}
          menuPosition={isOverModal && "fixed"}
          //menuIsOpen={true}
          {...field}
          onChange={handleChange || field?.onChange}
          ref={selectRef}
          styles={maxHeight > 0 ? customStylesScroll : customStyles}
          options={options}
          placeholder={placeholder || "Selecione"}
          value={getValues()}
          defaultValue={defaultValue}
        />
        {hasError && <div className="select-input-invalid-feedback">{hasError}</div>}
      </div>
    )
  }

  return (
    <div className={`${breakLine ? "react-select-container" : "react-select-container-no-wrap"}`}>
      <Controller
        name={name}
        control={control}
        rules={{
          required: {
            value: isRequired,
            message: "Campo Obrigatório",
          },
        }}
        render={({ field }) => {
          return (
            <Select
              {...field}
              className={`options-select ${isMulti ? "multi" : ""} ${breakLine ? "" : "no-wrap"} ${className}`}
              classNamePrefix="options"
              components={customComponents}
              options={options}
              isClearable={isClearable}
              isDisabled={isDisabled}
              isMulti={isMulti}
              isLoading={isLoading}
              defaultValue={defaultValue && defaultValue.value ? defaultValue : ''}
              selectedValue={selectedValue}
              menuShouldBlockScroll={menuShouldBlockScroll}
              menuPortalTarget={isOverModal && document.body}
              menuPosition={isOverModal && "fixed"}
              //menuIsOpen={true}
              value={getValues()}
              onChange={(data) => {
                if (data?.value) {
                  field.onChange(data.value)
                  handleChange && handleChange(data || data?.value)
                } else {
                  field.onChange(data)
                  handleChange && handleChange(data)
                }
              }}
              closeMenuOnSelect={closeMenuOnSelect}
              placeholder={placeholder || "Selecione"}
              styles={maxHeight > 0 ? customStylesScroll : customStyles}
              isSearchable={isSearchable}
              {...register}
              ref={selectRef}
            />
          )
        }}
      />
      {hasError && <div className="select-input-invalid-feedback">{hasError}</div>}
    </div>
  )
}

export const SelectInputAsync = ({
  whiteVariation,
  isMulti = false,
  isClearable = false,
  onChange,
  placeholder,
  selectedValue,
  loadOptions,
  loadingMessage = "Aguarde, estamos buscando suas respostas",
  noOptionsMessage = "Faça uma nova pergunta",
}) => {
  const customStyles = {
    control: (base, state) => ({
      ...base,
      background: !whiteVariation ? "#EFEFEF" : "#ffff !important",
      fontSize: "13px",
      lineHeight: "150%",
      color: !whiteVariation ? "#87938A" : "#394F3F !important",
      // Overwrittes the different states of border
      border: "none",
      // Removes weird border around container
      boxShadow: state.isFocused ? null : null,
      "&:hover": {
        // Overwrittes the different states of border
        borderColor: state.isFocused ? "red" : "blue",
      },
    }),
    menuList: (base) => ({
      ...base,
      "::-webkit-scrollbar": {
        width: "3px",
      },
      "::-webkit-scrollbar-track": {
        background: "#EFEFEF",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#c3c3c3",
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#c3c3c3",
      },
      paddingTop: -1,
      paddingBottom: -1,
      marginLeft: 0,
      marginRight: 0,
      marginBottom: 0,
      marginTop: 3,
      border: "1px solid #EFEFEF",
      borderRadius: "4px",
    }),
    noOptionsMessage: (base) => ({
      ...base,
    }),
  }

  return (
    <AsyncSelect
      className={`options-select async-select ${isMulti ? "multi" : ""}`}
      classNamePrefix="options"
      components={{ DropdownIndicator }}
      styles={customStyles}
      loadOptions={loadOptions}
      onChange={(data) => {
        data && onChange(data)
      }}
      value={selectedValue || ""}
      placeholder={placeholder || "Selecione"}
      loadingMessage={() => loadingMessage}
      noOptionsMessage={() => noOptionsMessage}
      //defaultValue={[]}
      //isMulti={isMulti}
      //isClearable={isClearable}
      cacheOptions
      //defaultOptions={[]}
    />
  )
}

export const SelectInputCreatable = ({
  options,
  name,
  control,
  isRequired = false,
  handleChange,
  placeholder,
  isMulti,
  whiteVariation,
  closeMenuOnSelect = true,
  menuShouldBlockScroll = false,
  isSearchable = false,
  isDisabled = false,
  isClearable = false,
  defaultValue,
  isLoading,
  selectedValue,
  hasError = false,
  value,
  ref,
  register,
  findValue = false,
  resetFields,
  ...field
}) => {
  const customStyles = {
    control: (base, state) => ({
      ...base,
      background: !whiteVariation ? "#EFEFEF" : "#ffff !important",
      fontSize: "13px",
      lineHeight: "150%",
      color: !whiteVariation ? "#87938A" : "#394F3F !important",
      // Overwrittes the different states of border
      border: "none",
      // Removes weird border around container
      boxShadow: state.isFocused ? null : null,
      "&:hover": {
        // Overwrittes the different states of border
        borderColor: state.isFocused ? "red" : "blue",
      },
    }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999999 }),
    menu: (provided) => ({ ...provided, zIndex: 999999 }),
    menuList: (base) => ({
      ...base,
      "::-webkit-scrollbar": {
        width: "3px",
      },
      "::-webkit-scrollbar-track": {
        background: "#EFEFEF",
      },
      "::-webkit-scrollbar-thumb": {
        background: "#c3c3c3",
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: "#c3c3c3",
      },
      paddingTop: -1,
      paddingBottom: -1,
      marginLeft: 0,
      marginRight: 0,
      marginBottom: 0,
      marginTop: 3,
      border: "1px solid #EFEFEF",
      borderRadius: "4px",
    }),
    noOptionsMessage: (base) => ({
      ...base,
    }),
  }

  const selectRef = useRef(null)

  const onClear = () => {
    selectRef?.current?.clearValue()
  }

  useEffect(() => {
    if (resetFields) {
      onClear()
    }
  }, [resetFields])

  if (!control) {
    return (
      <>
        <CreatableSelect
          className={`options-select ${isMulti ? "multi" : ""}`}
          classNamePrefix="options"
          components={{ DropdownIndicator, NoOptionsMessage }}
          isSearchable={isSearchable}
          isClearable={isClearable}
          isDisabled={isDisabled}
          isMulti={isMulti}
          isLoading={isLoading}
          menuPortalTarget={document.body}
          menuPosition={"fixed"}
          menuShouldBlockScroll={menuShouldBlockScroll}
          {...field}
          ref={selectRef}
          styles={customStyles}
          options={options}
          placeholder={placeholder || "Selecione"}
          value={options && options.find((c) => c?.value === value)}
          defaultValue={defaultValue}
        />
        {hasError && <div className="select-input-invalid-feedback">{hasError}</div>}
      </>
    )
  }

  return (
    <>
      <Controller
        name={name}
        control={control}
        rules={{
          required: {
            value: isRequired,
            message: "Campo Obrigatório",
          },
        }}
        render={({ field }) => {
          return (
            <CreatableSelect
              {...field}
              className={`options-select ${isMulti ? "multi" : ""}`}
              classNamePrefix="options"
              options={options}
              isClearable={isClearable}
              isDisabled={isDisabled}
              isMulti={isMulti}
              isLoading={isLoading}
              defaultValue={defaultValue}
              selectedValue={selectedValue}
              value={selectedValue && options?.find((c) => c.value === field?.value)}
              onChange={(data) => {
                if (data?.value) {
                  field.onChange(data.value)
                  handleChange && handleChange(data || data?.value)
                } else {
                  field.onChange(data)
                  handleChange && handleChange(data)
                }
              }}
              closeMenuOnSelect={closeMenuOnSelect}
              placeholder={placeholder || "Selecione"}
              styles={customStyles}
              components={{ DropdownIndicator, NoOptionsMessage }}
              isSearchable={isSearchable}
              {...register}
              inputRef={field.ref}
            />
          )
        }}
      />
      {hasError && <div className="select-input-invalid-feedback">{hasError}</div>}
    </>
  )
}

export const CheckBox = ({
  title = "",
  onChange,
  checked,
  label,
  value,
  name,
  style,
  disabled,
  ...props
}) => {
  return (
    <div
      className={`checkbox-container${disabled ? " --disabled" : ""}`}
      title={title}
      {...{ style }}>
      <input
        type="checkbox"
        name={(name && name) || "check"}
        value={value}
        checked={checked}
        className="checkbox-input"
        onChange={onChange}
        disabled={disabled}
        {...props}
      />
      <label htmlFor="check">{label}</label>
    </div>
  )
}
